import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ButtonModule } from 'primeng/button';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { CompletionStatus, PunchCreateEditMode } from '@completion/enums';
import { Project } from '@completion/models';
import { getCurrentProject } from '@completion/selectors';
import { PunchService, TagSearchResponse, TagSearchResult } from '@completion/services';
import { State } from '@completion/reducers';
import { PunchDialogComponent } from '../../shared/punch-dialog/punch-dialog.component';
import {
  ChecksheetAssignmentsTableComponent,
  ChecksheetAssignment,
  AssignmentNavigationEvent,
} from '../../shared/checksheet-assignments-table';

@Component({
  selector: 'app-search-result-dialog',
  standalone: true,
  imports: [CommonModule, ButtonModule, ChecksheetAssignmentsTableComponent],
  templateUrl: './search-result-dialog.component.html',
  styleUrls: ['./search-result-dialog.component.scss'],
})
export class SearchResultDialogComponent {
  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly punchService: PunchService,
    private readonly store: Store<State>,
    public dialogRef: MatDialogRef<SearchResultDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TagSearchResponse,
  ) {}

  get results(): TagSearchResult[] {
    return this.data?.result ?? [];
  }

  get resultMessage(): string | null {
    return this.data?.resultMessage ?? null;
  }

  get assignmentData(): ChecksheetAssignment[] {
    return this.results.map((result) => ({
      id: result.csaId ?? 0,
      cpId: result.cpId,
      mcpId: result.mcpId,
      cpNumber: result.cpNumber,
      mcpNumber: result.mcpNumber,
      tagNumber: result.tagNumber,
      checksheetId: result.csaId,
      checksheetNumber: result.csNumber,
      tagName: result.name,
      checksheetType: 'MC', // SearchResult doesn't have type, but we know it's MC
      status: result.status,
      punchCount: result.punchCount,
      punchIdsCsv: result.punchIdsCsv,
      signed: result.signed,
      signedByUserName: result.signedByUserName,
      signedByTimestamp: result.signedByTimestamp,
      verified: result.verified,
      verifiedByUserName: result.verifiedByUserName,
      verifiedByTimestamp: result.verifiedByTimestamp,
      engineeringStatus: result.engineeringStatus,
    }));
  }

  displayColumns = ['cpNumber', 'mcpNumber', 'tagNumber', 'checksheetNumber', 'status', 'progress', 'punchCount', 'signed', 'verified'];

  isVoided(item: TagSearchResult): boolean {
    return item.engineeringStatus === 'V';
  }

  getCompletionStatus(status: string): CompletionStatus {
    return CompletionStatus[status as keyof typeof CompletionStatus];
  }

  progressPct(item: TagSearchResult): number {
    return item.progress != null ? Math.round(item.progress * 100) : 0;
  }

  onAssignmentNavigation(event: AssignmentNavigationEvent): void {
    const assignment = event.assignment;
    const result = this.results.find((r) => r.csaId === assignment.id);
    if (!result) return;

    switch (event.type) {
      case 'cp':
        this.navToCp(result);
        break;
      case 'mcp':
        this.navToMcp(result);
        break;
      case 'checksheet':
        this.handleChecksheetNavigation(result);
        break;
    }
  }

  private handleChecksheetNavigation(item: TagSearchResult): void {
    const firstPunchId = this.getFirstPunchId(item);
    if (firstPunchId) {
      this.openPunch(item);
    } else {
      this.navToCsa(item);
    }
  }

  openPunch(item: TagSearchResult): void {
    const firstPunchId = this.getFirstPunchId(item);
    if (!firstPunchId) {
      return;
    }

    this.store
      .select(getCurrentProject)
      .pipe(take(1))
      .subscribe((project: Project | null) => {
        if (!project?.id) {
          return;
        }

        this.punchService
          .getPunchById(project.id, firstPunchId)
          .pipe(take(1))
          .subscribe((punch) => {
            this.dialog.open(PunchDialogComponent, {
              data: { punch: { ...punch }, editMode: PunchCreateEditMode.EditPunch },
              panelClass: 'punch-dialog-panel',
              maxHeight: '92vh',
            });
          });
      });
  }

  navToCp(item: TagSearchResult): void {
    if (!item.cpId) return;
    this.router.navigate([`/cp/${item.cpId}`]);
    this.close();
  }

  navToMcp(item: TagSearchResult): void {
    if (!item.cpId || !item.mcpId) return;
    this.router.navigate([`/cp/${item.cpId}/mcp/${item.mcpId}`]);
    this.close();
  }

  navToCsa(item: TagSearchResult): void {
    if (!item.csaId || !item.cpId) return;
    if (item.mcpId) {
      this.router.navigate([`/cp/${item.cpId}/mcp/${item.mcpId}/checksheet/${item.csaId}`]);
    } else {
      this.router.navigate([`/cp/${item.cpId}/checksheet/${item.csaId}`]);
    }
    this.close();
  }

  private getFirstPunchId(item: TagSearchResult): number | null {
    const ids = this.getPunchIds(item);
    return ids.length > 0 ? ids[0] : null;
  }

  private getPunchIds(item: TagSearchResult): number[] {
    if (!item.punchIdsCsv) {
      return [];
    }
    return item.punchIdsCsv
      .split(',')
      .map((part) => Number(part.trim()))
      .filter((id) => Number.isInteger(id) && id > 0);
  }

  close(): void {
    this.dialogRef.close();
  }
}
